import { useState } from 'preact/hooks';
import { useStore } from '../store';
import { Sheet } from './Sheet';

const DARTS = [['light', 'Light'], ['standard', 'Standard'], ['heavy', 'Heavy']] as const;
const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1).replace(/_/g, ' ');

/**
 * Field capture: dart or net a spotted animal, crate a downed one, and see traps and crate journeys.
 * Commands: field.dart, field.net, field.crate (docs/COMMANDS.md). Rejections arrive as alerts;
 * outcomes come from ui.field (lastDart / lastNet / held / sedated), never from a local guess.
 */
export function FieldCapture({ onClose }: { onClose(): void }) {
  const { sim, bridge } = useStore();
  const fc = sim.fieldCapture;
  const [express, setExpress] = useState(false);
  const down = fc ? [...new Set([...fc.sedatedAnimalIds, ...fc.heldAnimalIds])] : [];
  const crate = (animalId: string) => { bridge.send('field.crate', { animalId, mode: express ? 'express' : 'rest' }); };
  const nameOf = (id: string) => {
    const w = fc?.wildAnimals?.find((x) => x.animalId === id) ?? fc?.spotMarkers.find((x) => x.animalId === id);
    return w?.displayName ?? `Animal ${id}`;
  };
  const last = fc?.lastDart ?? null;
  const lastNet = fc?.lastNet ?? null;
  return (
    <Sheet label="Capture" onClose={onClose}>
      <div style="padding:6px 16px 18px">
        <div class="eyebrow eyebrow-rule">Capture</div>
        {fc === null && <p style="font-size:13px;color:var(--ui-ink-soft)">Field capture opens once the scenario starts.</p>}
        {fc && (
          <>
            <div class="eyebrow" style="margin-top:10px">Spotted animals</div>
            {fc.spotMarkers.length === 0 && <div class="row-label" style="margin-top:4px">Nothing spotted. Use Spot or Track first.</div>}
            {fc.spotMarkers.map((m) => (
              <div key={m.animalId} class="card" style="padding:10px 12px;margin-top:6px">
                <div class="row-value" style="font-size:16px">{nameOf(m.animalId)} - {m.distanceM} m ({m.tag})</div>
                <div style="display:flex;gap:6px;margin-top:8px;flex-wrap:wrap">
                  {DARTS.map(([id, label]) => <button key={id} class="btn btn-quiet" style="padding:6px 10px;font-size:11px" onClick={() => { bridge.send('field.dart', { animalId: m.animalId, dartType: id }); }}>Dart: {label}</button>)}
                  <button class="btn btn-quiet" style="padding:6px 10px;font-size:11px" onClick={() => { bridge.send('field.net', { animalId: m.animalId }); }}>Net gun</button>
                </div>
              </div>
            ))}
            {(last || lastNet) && (
              <div class="row-label" style="white-space:normal;margin-top:6px">
                {last ? `Last dart: ${last.hit ? 'hit' : 'missed'} (${last.dartType}) on ${nameOf(last.animalId).toLowerCase()}.` : ''} {lastNet ? `Last net: ${lastNet.hit ? 'held' : 'missed'} ${nameOf(lastNet.animalId).toLowerCase()}.` : ''}
              </div>
            )}
            <div class="eyebrow" style="margin-top:12px">Downed animals</div>
            {down.length === 0 && <div class="row-label" style="margin-top:4px">None. Crate a darted or netted animal within 10 m before it wakes.</div>}
            {down.map((id) => (
              <div key={id} class="card row" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:6px">
                <span class="row-main"><div class="row-value">{nameOf(id)}</div><div class="row-label">{fc.heldAnimalIds.includes(id) ? 'Held by the net' : 'Sedated'}</div></span>
                <button class="btn btn-primary" style="padding:6px 12px;font-size:11px" onClick={() => { crate(id); }}>Crate</button>
              </div>
            ))}
            {down.length > 0 && (
              <div class="seg" role="group" aria-label="Crate journey" style="margin-top:8px">
                <button class={express ? '' : 'on'} onClick={() => { setExpress(false); }}>Rest stops</button>
                <button class={express ? 'on' : ''} onClick={() => { setExpress(true); }}>Express</button>
              </div>
            )}
            <div class="eyebrow" style="margin-top:12px">Field traps</div>
            {fc.fieldTraps.length === 0 && <div class="row-label" style="margin-top:4px">No traps set</div>}
            {fc.fieldTraps.map((t) => (
              <div key={t.id} class="card row" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:6px">
                <span class="row-main"><div class="row-value">{cap(t.typeId)}</div><div class="row-label">{t.state === 'holding' ? `Holding a ${nameOf(t.holdingAnimalId ?? '').toLowerCase()}` : t.state === 'armed' ? 'Armed' : 'Sprung, empty'}</div></span>
                {t.state === 'holding' && t.holdingAnimalId && <button class="btn btn-primary" style="padding:6px 12px;font-size:11px" onClick={() => { crate(t.holdingAnimalId ?? ''); }}>Crate</button>}
              </div>
            ))}
            {fc.inTransit.length > 0 && <div class="eyebrow" style="margin-top:12px">In transit</div>}
            {fc.inTransit.map((c) => (
              <div key={c.crateId} class="row-label" style="margin-top:4px">{cap(c.species)} - leg {c.legsDone}/{c.legsTotal} - {c.ticksToArrival} h to arrival ({c.mode})</div>
            ))}
          </>
        )}
      </div>
    </Sheet>
  );
}
